import React from "react";
import { useTranslation } from "react-i18next";
import ImageUploader from "@/components/uploads/ImageUploader";

/**
 * Shared profile identity fields, rendered identically by the onboarding flow
 * and the Settings profile editor so both collect the exact same set — no datum
 * is asked twice.
 *
 * Operates on a flat form object with keys: avatar_url, full_name, company_name,
 * city, state, zip, bio. It merges partial updates through `onChange(patch)`.
 *
 * Phone is injected via `phoneSlot` because each caller renders it differently
 * (onboarding wires it to an OTP verification flow; Settings uses a plain input).
 * Provider payment links stay a Settings-only section (post-onboarding setup).
 *
 * @param {object}   form      current form values
 * @param {function} onChange  (patch) => void — merges a partial update
 * @param {string}   role      "provider" | "client"
 * @param {React.ReactNode} phoneSlot  the caller's phone field (occupies one grid cell)
 */
export default function ProfileFields({ form, onChange, role, phoneSlot }) {
  const { t } = useTranslation();
  const isProvider = role === "provider";

  return (
    <div data-testid="profile-fields">
      <div className="mb-6">
        {/* Many FixyBees are businesses and upload a logo rather than a
            personal photo, so providers get a label that covers both. */}
        <label className="label-base">
          {isProvider ? t("settings.profile.photoProvider") : t("settings.profile.photo")}
        </label>
        <div className="max-w-[180px]">
          <ImageUploader
            value={form.avatar_url}
            purpose="profile_photo"
            label={t("common.uploadPhoto")}
            aspect="square"
            testid="profile-photo"
            onUploaded={({ url }) => onChange({ avatar_url: url })}
            onCleared={() => onChange({ avatar_url: "" })}
          />
        </div>
        <p className="text-xs text-smoke mt-2">{t("settings.profile.photoHint")}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ProfileField
          label={t("settings.profile.fullName")}
          value={form.full_name}
          onChange={(v) => onChange({ full_name: v })}
          testid="profile-name"
        />
        {phoneSlot}
        {isProvider && (
          <ProfileField
            label={t("settings.profile.company")}
            value={form.company_name}
            onChange={(v) => onChange({ company_name: v })}
            testid="profile-company"
          />
        )}
        <ProfileField
          label={t("settings.profile.city")}
          value={form.city}
          onChange={(v) => onChange({ city: v })}
          testid="profile-city"
        />
        <ProfileField
          label={t("settings.profile.state")}
          value={form.state}
          onChange={(v) => onChange({ state: v })}
          testid="profile-state"
          placeholder={t("settings.profile.statePlaceholder")}
        />
        {/* The ZIP placeholder was the literal "33101" — downtown Miami — on the
            one form every user fills in. The marketplace's own FixyBees moved to
            El Paso on 2026-08-12 and nothing here is in Florida any more, so the
            example pointed at a city we do not serve. A shape, not a specimen:
            a real-looking value in a field is something people copy. */}
        <ProfileField
          label={t("settings.profile.zip")}
          value={form.zip}
          onChange={(v) => onChange({ zip: v })}
          testid="profile-zip"
          placeholder={t("settings.profile.zipPlaceholder")}
          inputMode="numeric"
          maxLength={5}
          hint={isProvider ? t("settings.profile.zipHint") : undefined}
        />
      </div>

      <div className="mt-4">
        <label className="label-base">{t("settings.profile.description")}</label>
        <textarea
          rows={3}
          value={form.bio || ""}
          onChange={(e) => onChange({ bio: e.target.value })}
          className="input-base resize-none"
          maxLength={500}
          placeholder={t("settings.profile.descriptionPlaceholder")}
          data-testid="profile-bio"
        />
      </div>
    </div>
  );
}

function ProfileField({ label, value, onChange, testid, hint, placeholder, maxLength, inputMode }) {
  return (
    <div>
      <label className="label-base">{label}</label>
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="input-base"
        data-testid={testid}
        placeholder={placeholder}
        maxLength={maxLength}
        inputMode={inputMode}
      />
      {hint && <p className="text-xs text-smoke mt-1">{hint}</p>}
    </div>
  );
}
